import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Input,
  FormControl,
  InputLabel,
  Box,
  Divider,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    marginBottom: theme.spacing(0.5),
  },
  sectionHelp: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginBottom: theme.spacing(1.5),
    display: "block",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  keywordRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
}));

const FlowTriggerModal = ({ open, onClose, flow, onSaved }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connections, setConnections] = useState([]);

  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [keywordType, setKeywordType] = useState("exact");
  const [whatsappIds, setWhatsappIds] = useState([]);
  const [welcomeIds, setWelcomeIds] = useState([]);
  const [firstContactIds, setFirstContactIds] = useState([]);

  const cargarConexiones = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp");
      setConnections(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Precargar el estado actual del flujo cada vez que se abre
  useEffect(() => {
    if (!open || !flow) return;
    cargarConexiones();

    const primerTrigger = (flow.triggers || [])[0];
    setKeywords(flow.keywords || []);
    setKeywordType(primerTrigger?.type || "exact");
    setWhatsappIds(primerTrigger?.whatsappIds || []);
    setWelcomeIds((flow.welcomeConnections || []).map((c) => c.id));
    setFirstContactIds((flow.firstContactConnections || []).map((c) => c.id));
    setKeywordInput("");
  }, [open, flow, cargarConexiones]);

  const agregarKeyword = () => {
    const valor = keywordInput.trim();
    if (!valor) return;
    if (keywords.some((k) => k.toLowerCase() === valor.toLowerCase())) {
      setKeywordInput("");
      return;
    }
    setKeywords([...keywords, valor]);
    setKeywordInput("");
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      agregarKeyword();
    }
  };

  const quitarKeyword = (valor) => {
    setKeywords(keywords.filter((k) => k !== valor));
  };

  const handleGuardar = async () => {
    // Si hay palabras clave, debe indicarse al menos una conexión
    if (keywords.length > 0 && whatsappIds.length === 0) {
      toast.warn(
        "Seleccione al menos una conexión donde funcionará la palabra clave."
      );
      return;
    }

    setSaving(true);
    try {
      await api.put(`/flowbuilder/${flow.id}/trigger`, {
        keywords,
        keywordType,
        whatsappIds,
        welcomeConnectionIds: welcomeIds,
        firstContactConnectionIds: firstContactIds,
      });
      toast.success("Disparador y conexiones guardados.");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const renderSelectConexiones = (label, value, setValue) => (
    <FormControl variant="outlined" fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => setValue(e.target.value)}
        input={<Input />}
        label={label}
        renderValue={(selected) =>
          selected.length === 0
            ? "Ninguna"
            : selected
                .map((id) => connections.find((c) => c.id === id)?.name || id)
                .join(", ")
        }
      >
        {connections.length === 0 && (
          <MenuItem disabled value="">
            No hay conexiones disponibles
          </MenuItem>
        )}
        {connections.map((conn) => (
          <MenuItem key={conn.id} value={conn.id}>
            <Checkbox checked={value.indexOf(conn.id) > -1} color="primary" />
            <ListItemText primary={conn.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Disparador y conexiones
        <Typography variant="body2" color="textSecondary">
          {flow?.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* --- Palabra clave --- */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Palabras clave
              </Typography>
              <Typography className={classes.sectionHelp}>
                El flujo se inicia cuando el cliente escribe alguna de estas
                palabras. Escriba y presione Enter para agregar.
              </Typography>

              <div className={classes.keywordRow}>
                <TextField
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={handleKeywordKeyDown}
                  placeholder="Ej: precio, horario, casa"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <Button onClick={agregarKeyword} variant="outlined" size="medium">
                  Agregar
                </Button>
              </div>

              {keywords.length > 0 && (
                <div className={classes.chips}>
                  {keywords.map((k) => (
                    <Chip
                      key={k}
                      label={k}
                      onDelete={() => quitarKeyword(k)}
                      color="primary"
                      size="small"
                    />
                  ))}
                </div>
              )}

              {keywords.length > 0 && (
                <Box mt={2}>
                  <Typography className={classes.sectionHelp}>
                    ¿Cómo debe coincidir el mensaje del cliente?
                  </Typography>
                  <RadioGroup
                    row
                    value={keywordType}
                    onChange={(e) => setKeywordType(e.target.value)}
                  >
                    <FormControlLabel
                      value="exact"
                      control={<Radio color="primary" size="small" />}
                      label="Exacta"
                    />
                    <FormControlLabel
                      value="partial"
                      control={<Radio color="primary" size="small" />}
                      label="Contiene la palabra"
                    />
                  </RadioGroup>

                  <Box mt={1}>
                    {renderSelectConexiones(
                      "Conexiones donde funciona",
                      whatsappIds,
                      setWhatsappIds
                    )}
                  </Box>
                </Box>
              )}
            </div>

            <Divider />

            {/* --- Flujo de bienvenida --- */}
            <div className={classes.section} style={{ marginTop: 24 }}>
              <Typography className={classes.sectionTitle}>
                Flujo de bienvenida
              </Typography>
              <Typography className={classes.sectionHelp}>
                Se ejecuta en el primer mensaje de un contacto que ya existe en
                la base.
              </Typography>
              {renderSelectConexiones(
                "Conexiones",
                welcomeIds,
                setWelcomeIds
              )}
            </div>

            {/* --- Flujo de primer contacto --- */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Flujo de primer contacto
              </Typography>
              <Typography className={classes.sectionHelp}>
                Se ejecuta cuando escribe un contacto nuevo y su mensaje no
                coincide con ninguna palabra clave.
              </Typography>
              {renderSelectConexiones(
                "Conexiones",
                firstContactIds,
                setFirstContactIds
              )}
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          color="primary"
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowTriggerModal;
